import moment from 'moment';
import {
  OrderHistoryItem,
  OrderHistoryResponse,
  PlaceOrderRequest,
  PlaceOrderRequestDish
} from '../../services/logistics/types';
import {
  DeliveryInfo,
  DeliveryInfoDeliveryMoment,
  DeliveryInfoDish,
  DeliveryInfoResponse,
  DishCategoryResponse
} from '../../services/website/types';
import { DishFormValue, FormValues, SelectableDeliveryMoment, ValidationResult } from './types';

const isDeliveryMomentUsed = (
  orderHistory: OrderHistoryResponse,
  deliveryId: number,
  deliveryMomentId: number
) => {
  return orderHistory.some(
    (item) => item.delivery_id === deliveryId && item.delivery_moment_id === deliveryMomentId
  );
};

export const hasDeliveryMomentExpired = (deliveryMoment: DeliveryInfoDeliveryMoment) => {
  const diffInDays = moment(deliveryMoment.closing_time).diff(moment(), 'days');

  return diffInDays < 3;
};

export const getDeliveryMoments = (
  dishCategories: DishCategoryResponse,
  deliveryInfo: DeliveryInfoResponse,
  orderHistory: OrderHistoryResponse
) => {
  return deliveryInfo.reduce<ReadonlyArray<SelectableDeliveryMoment>>(
    (previousValue, currentValue) => {
      const deliveryMoments = [...currentValue.deliveryMoments]
        .sort((a, b) => {
          return (
            new Date(`${a.delivery_date} ${a.delivery_time_start}`).getDate() -
            new Date(`${b.delivery_date} ${b.delivery_time_start}`).getDate()
          );
        })
        .map((deliveryMoment) => ({
          mealOverviewId: currentValue.mealOverviewId,
          deliveryId: currentValue.deliveryId,
          deliveryMomentId: deliveryMoment.deliveryMomentId,
          camasysDeliveryDate: deliveryMoment.camasys_delivery_date,
          deliveryDate: moment(deliveryMoment.delivery_date),
          deliveryTimeStart: deliveryMoment.delivery_time_start,
          deliveryTimeEnd: deliveryMoment.delivery_time_end,
          selectable:
            !hasDeliveryMomentExpired(deliveryMoment) &&
            !isDeliveryMomentUsed(
              orderHistory,
              currentValue.deliveryId,
              deliveryMoment.deliveryMomentId
            ),
          categories: dishCategories
            .map((dishCategory) => {
              const dishesForCategory = currentValue.dishes.filter(
                (dish) => dish.dish_category_id === dishCategory.id
              );

              return {
                name: dishCategory.name,
                dishes: dishesForCategory.map((dish) => {
                  return {
                    id: dish.id,
                    camasysId: dish.camasys_id,
                    name: dish.name,
                    dishNumber: dish.dish_number_display_name,
                    image: dish.image_small,
                    portions: dish.dish_portions
                      .map((portion) => ({
                        camasysId: portion.camasys_portion_id,
                        name: portion.name,
                        description: `${portion.name} (${portion.amount} ${portion.amount_unit})`,
                        amount: portion.amount,
                        amountUnit: portion.amount_unit,
                        price: portion.price_inclusive_vat
                      }))
                      .sort((a, b) => a.amount - b.amount)
                  };
                })
              };
            })
            .filter((category) => category.dishes.length > 0)
        }));

      return previousValue.concat(deliveryMoments);
    },
    []
  );
};

export const findDeliveryInfoByDeliveryId = (
  deliveryInfos: ReadonlyArray<DeliveryInfo>,
  deliveryId: number
): DeliveryInfo | null => {
  return deliveryInfos.find((deliveryInfo) => deliveryInfo.deliveryId === deliveryId) || null;
};

export const findDeliveryMoment = (
  deliveryMoments: ReadonlyArray<DeliveryInfoDeliveryMoment>,
  deliveryMomentId: number
): DeliveryInfoDeliveryMoment | null => {
  return (
    deliveryMoments.find(
      (deliveryMoment) => deliveryMoment.deliveryMomentId === deliveryMomentId
    ) || null
  );
};

export const getSelectedDeliveryMoment = (
  selectableDeliveryMoments: ReadonlyArray<SelectableDeliveryMoment>,
  selectedDeliveryId: number,
  selectedDeliveryMomentId: number
): SelectableDeliveryMoment | null => {
  return (
    selectableDeliveryMoments.find(
      (selectableDeliveryMoment) =>
        selectableDeliveryMoment.deliveryId === selectedDeliveryId &&
        selectableDeliveryMoment.deliveryMomentId === selectedDeliveryMomentId
    ) || null
  );
};

const MAALTIJD_TYPE_MAALTIJDSERVICE = 'maaltijdservice';
const MAALTIJD_TYPE_MAALTIJDPAKKETTEN = 'maaltijdpakket';

const isCamasysMealAMealbox = (matchingDish: DeliveryInfoDish) =>
  matchingDish &&
  matchingDish.type &&
  matchingDish.type.toLowerCase() === MAALTIJD_TYPE_MAALTIJDPAKKETTEN;

export const getMainMealCountForDishSingle = (matchingDish: DeliveryInfoDish, quantity: number) => {
  const acceptedList = [MAALTIJD_TYPE_MAALTIJDSERVICE, MAALTIJD_TYPE_MAALTIJDPAKKETTEN];

  if (acceptedList.includes(matchingDish.type.toLowerCase())) {
    if (isCamasysMealAMealbox(matchingDish)) {
      if (matchingDish.ingredients && matchingDish.ingredients.length > 0) {
        const mainMealIngredients = matchingDish.ingredients.filter(
          //ingredients can be submeals in mealboxes if type = maaltijdservice
          (ingredient) => ingredient.type.toLowerCase() === MAALTIJD_TYPE_MAALTIJDSERVICE
        );
        if (mainMealIngredients.length === 0) {
          return quantity * 5;
        }
        return quantity * mainMealIngredients.length;
      } else {
        return quantity * 5;
      }
    } else {
      return quantity;
    }
  }

  return 0;
};

export const getMainMealCountForDish = (
  matchingDish: DeliveryInfoDish,
  portionFormValues: { quantity: number }[]
) => {
  return portionFormValues.reduce((mainMealCount, portionFormValue) => {
    return mainMealCount + getMainMealCountForDishSingle(matchingDish, portionFormValue.quantity);
  }, 0);
};

const getMainMealCount = (deliveryInfo: DeliveryInfo, dishFormValues: DishFormValue[]): number => {
  return dishFormValues.reduce((totalMainMealCount, dishFormValue) => {
    const matchingDish = deliveryInfo.dishes.find(
      (dish) => dish.camasys_id === dishFormValue.camasysid
    );

    if (!matchingDish) {
      return totalMainMealCount;
    }

    const portions = Object.values(dishFormValue.portions).filter(
      (portion) => !isNaN(portion.quantity)
    );
    const dishMainMealCount = getMainMealCountForDish(matchingDish, portions);

    return totalMainMealCount + dishMainMealCount;
  }, 0);
};

export const getPlaceOrderValidationResult = (
  deliveryInfo: DeliveryInfo | null,
  values: FormValues
): ValidationResult => {
  if (!deliveryInfo) {
    return {
      isValid: false,
      message: 'Er is geen levermoment geselecteerd'
    };
  }

  const dishes = Object.values(values.dishes);
  const selectedDishes = dishes.filter((dish) => dish.selected);

  if (selectedDishes.length === 0) {
    return {
      isValid: false,
      message: 'Er zijn geen maaltijden gekozen'
    };
  }

  const selectedDishesWithoutPortions = selectedDishes.filter((selectedDish) => {
    const portions = Object.values(selectedDish.portions);
    return !portions.some((portion) => portion.quantity > 0);
  });

  if (selectedDishesWithoutPortions.length > 0) {
    return {
      isValid: false,
      message: 'Er is een maaltijd gekozen waarvoor geen aantal is ingevoerd'
    };
  }

  const mainMealCount = getMainMealCount(deliveryInfo, selectedDishes);

  if (mainMealCount < 2) {
    return {
      isValid: false,
      message: 'Er moeten minimaal 2 maaltijden besteld worden in een levering.'
    };
  }

  return { isValid: true };
};

export const getPlaceOrderRequest = (
  customerId: number,
  selectedDeliveryMoment: SelectableDeliveryMoment,
  values: FormValues,
  existingOrder?: OrderHistoryItem
): PlaceOrderRequest => {
  return {
    routeComments: values.route_comments,
    voucherCode: values.voucher_code,
    mealOverviewId: selectedDeliveryMoment.mealOverviewId,
    deliveryId: selectedDeliveryMoment.deliveryId,
    deliveryMomentId: selectedDeliveryMoment.deliveryMomentId,
    camasysDeliveryDate: selectedDeliveryMoment.camasysDeliveryDate,
    customerId: customerId,
    orderId: existingOrder ? existingOrder.order_id : null,
    orderNumber: existingOrder ? existingOrder.order_number : null,
    dishes: Object.values(values.dishes)
      .filter((dish) => dish.selected)
      .reduce<PlaceOrderRequestDish[]>((previousValue, currentValue) => {
        const portions = Object.values(currentValue.portions).filter(
          (portion) => portion.quantity > 0
        );

        const dishes = portions.map((portion) => ({
          camasysDishId: currentValue.camasysid,
          camasysDishPortionId: portion.camasysid,
          quantity: portion.quantity
        }));

        return previousValue.concat(dishes);
      }, [])
  };
};

export const hasMealWithMoreThanThreeDishes = (placeOrderRequest: PlaceOrderRequest) => {
  return placeOrderRequest.dishes.some((dish) => dish.quantity > 3);
};
